import Text from '@raydiant/raydial/components/Text';
import Button from '@raydiant/raydial/components/Button';
import TagIcon from '@raydiant/raydial/components/Icons/Tag';
import {
  BLACK_FRIDAY_PROMOTION_PLAN_COUNT,
  PROMOTION_PLAN_COUNT,
} from './constants';
import { isBlackFridayActive } from '../../utilities';
import analytics from '../../clients/analyticsClient';

interface NumberOfPlansProps {
  count: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
}

const NumberOfPlans = ({
  count,
  onChange,
  onBlur,
  onIncrement,
  onDecrement,
}: NumberOfPlansProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === '.' ||
      e.key === '+' ||
      e.key === '-' ||
      e.key === 'e' ||
      e.key === 'E'
    ) {
      e.preventDefault();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      onChange(e);
      return;
    }

    const numValue = parseInt(value, 10);

    if (!isNaN(numValue) && numValue >= 0) {
      if (
        isBlackFridayActive() &&
        numValue > BLACK_FRIDAY_PROMOTION_PLAN_COUNT
      ) {
        // segment tracking for black friday bulk order popup shown
        analytics.trackBulkOrderPopupShown({
          plan: 'pro',
          quantity: numValue,
          popupVariant: 'black_friday_bulk_order',
        });
        const limitedEvent = {
          ...e,
          target: {
            ...e.target,
            value: BLACK_FRIDAY_PROMOTION_PLAN_COUNT.toString(),
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(limitedEvent);
      } else {
        // segment tracking for regular bulk order popup shown
        analytics.trackBulkOrderPopupShown({
          plan: 'pro',
          quantity: numValue,
          popupVariant: 'regular_bulk_order',
        });
        onChange(e);
      }
    } else {
      onChange(e);
    }
  };

  const handleBlur = () => {
    if (isBlackFridayActive() && count > BLACK_FRIDAY_PROMOTION_PLAN_COUNT) {
      const syntheticEvent = {
        target: {
          value: BLACK_FRIDAY_PROMOTION_PLAN_COUNT.toString(),
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
    onBlur();
  };

  const handleIncrement = () => {
    if (isBlackFridayActive() && count >= BLACK_FRIDAY_PROMOTION_PLAN_COUNT) {
      return;
    }
    onIncrement();
  };

  const isIncrementDisabled =
    isBlackFridayActive() && count >= BLACK_FRIDAY_PROMOTION_PLAN_COUNT;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 text-black mt-4">
        <Text variant="smallSubheader">Number of plans</Text>
        <Text className="text-gray-600" variant="body">
          Enter the number of screens you want a plan for
        </Text>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <Button
          onPress={onDecrement}
          isDisabled={count <= 1}
          variant="unstyled"
          className="w-[38px] h-[39px] p-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          aria-label="Decrease"
        >
          <svg
            width="11"
            height="2"
            viewBox="0 0 11 2"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 0.825781C0 0.378906 0.34375 0.000781089 0.825 0.000781089H10.175C10.6219 0.000781089 11 0.378906 11 0.825781C11 1.30703 10.6219 1.65078 10.175 1.65078H0.825C0.34375 1.65078 0 1.30703 0 0.825781Z"
              fill="currentColor"
            />
          </svg>
        </Button>

        <input
          type="number"
          value={count === 0 ? '' : count}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          min="1"
          max={
            isBlackFridayActive()
              ? BLACK_FRIDAY_PROMOTION_PLAN_COUNT
              : undefined
          }
          className="w-[70px] h-[39px] rounded-lg p-2.5 bg-gray-100 text-center text-black font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />

        <Button
          onPress={handleIncrement}
          isDisabled={isIncrementDisabled}
          variant="unstyled"
          className="w-[38px] h-[39px] p-2.5 rounded-lg bg-blue-50 text-blue-600 hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          aria-label="Increase"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.9749 0.825V6.325H13.4749C13.9218 6.325 14.2999 6.70312 14.2999 7.15C14.2999 7.63125 13.9218 7.975 13.4749 7.975H7.9749V13.475C7.9749 13.9563 7.59678 14.3 7.1499 14.3C6.66865 14.3 6.3249 13.9563 6.3249 13.475V7.975H0.824902C0.343652 7.975 -9.79304e-05 7.63125 -9.79304e-05 7.15C-9.79304e-05 6.70312 0.343652 6.325 0.824902 6.325H6.3249V0.825C6.3249 0.378125 6.66865 -2.98023e-07 7.1499 -2.98023e-07C7.59678 -2.98023e-07 7.9749 0.378125 7.9749 0.825Z"
              fill="currentColor"
            />
          </svg>
        </Button>

        {isBlackFridayActive() && count >= BLACK_FRIDAY_PROMOTION_PLAN_COUNT && (
          <div className="flex items-center gap-1 flex-wrap bg-green-50 py-2.5 px-3 rounded-xl text-green-600">
            <TagIcon size={16} />
            <Text variant="body">
              {BLACK_FRIDAY_PROMOTION_PLAN_COUNT} plan max.
            </Text>
            <a
              href="https://www.displai.ai/black-friday/#form"
              target="_blank"
              onClick={() =>
                // segment tracking
                analytics.trackBulkOrderCTAClicked({
                  plan: 'pro',
                  quantity: count,
                  popupVariant: 'black_friday_bulk_order',
                })
              }
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-600 hover:underline font-bold"
            >
              <Text>Chat with sales for special offer</Text>
            </a>
          </div>
        )}

        {!isBlackFridayActive() && count >= PROMOTION_PLAN_COUNT && (
          <div className="flex items-center gap-1 flex-wrap bg-green-50 py-2.5 px-3 rounded-xl text-green-600">
            <TagIcon size={16} />
            <Text variant="body">Need 10+?</Text>
            <a
              href="https://www.displai.ai/book-a-demo/"
              target="_blank"
              onClick={() =>
                // segment tracking
                analytics.trackBulkOrderCTAClicked({
                  plan: 'pro',
                  quantity: count,
                  popupVariant: 'regular_bulk_order',
                })
              }
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-600 hover:underline font-bold"
            >
              <Text>Chat with sales for a discount</Text>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberOfPlans;
